import {ClickAwayListener} from '@mui/base/ClickAwayListener';
import {Add, ExpandMore} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  AutocompleteRenderInputParams,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  InputBase,
  Popper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers';
import moment from 'moment';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {
  IndividualPlan,
  IndividualPlanDiagnostic,
  IndividualPlanDiagnosticGoal,
  IndividualPlanDiagnosticGoalObjective,
  IndividualPlanDiagnosticGoalObjectiveCare,
} from '../../../../../../backend/interfaces/individual-plan';
import {UserPublicInfo} from '../../../../../../backend/responses/user-public-info';
import {AiButton} from '../../../../../../components/ai-button/ai-button';
import {
  ComboActions,
  ComboActionsProps,
} from '../../../../../../components/combo-actions/combo-actions';
import FlatInput from '../../../../../../components/flat-input/flat-input';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {Modal} from '../../../../../../components/modal/modal';
import {Option} from '../../../../../../components/role-autocomplete/autocomplete-input';
import {RoleAutocomplete} from '../../../../../../components/role-autocomplete/role-autocomplete';
import {AppContext} from '../../../../../../contexts/app/context';
import {AppContextType} from '../../../../../../contexts/app/types';
import {useAuth} from '../../../../../../contexts/auth/context';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {useSeniorSidebar} from '../../../../../../contexts/seniors-sidebar/context';
import {CaregiverTaskCompletion, ScreenPaths, ThemeMode} from '../../../../../../enums';
import {useObserveViewport} from '../../../../../../hooks';
import {translations} from '../../../../../../i18n/translations';
import {EntityUserIndividualPlansRepository} from '../../../../../../repositories/entity-user-individual-plans';
import {
  IndividualPlanCares,
  IndividualPlansRepository,
} from '../../../../../../repositories/individual-plans';
import {UsersRepository} from '../../../../../../repositories/users';
import {Pallete} from '../../../../../../theme/bluebird';
import {FiTrash2, FiEdit, FiArchive, FiClock} from 'react-icons/fi';
import {FlatSelectDropdown} from '../../../../../../components/flat-select-dropdown/flat-select-dropdown';
import {useFetchEntityUsers} from '../../../../../../hooks/use-fetch-entity-users';
import {UserType} from '../../../../../../backend/enums/user-type';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ActivityType} from '../../../../../../backend/interfaces/activity-type';
import ProgressLine from '../../../../../../components/progress-line';
import {IndividualPlanResults} from '../../../../../../backend/responses/individual-plan-results';
import {IndividualPlanAiGenerated} from '../../../../../../backend/responses/individual-plan-ai-generated';
import {IndividualPlanDiagnosticsBulkMode} from '../../../../../../backend/requests/individual-plan-diagnostics-bulk';
import {AiGenerateDialog} from './components/ai-generate-dialog';
import {IndividualPlanHistoryPanel} from './components/individual-plan-history-panel';
import {AiProposalPreview} from './components/ai-proposal-preview';

function stringToColor(str: string, prc: number = -10): string {
  // Generate a Hash for the String
  const hash = (word: string): number => {
    let h = 0;
    for (let i = 0; i < word.length; i++) {
      h = word.charCodeAt(i) + ((h << 5) - h);
    }
    return h;
  };

  // Change the darkness or lightness
  const shade = (color: string, prc: number): string => {
    const num = parseInt(color, 16);
    const amt = Math.round(2.55 * prc);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
    return ((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1);
  };

  // Convert int to RGBA (ensuring hex length is always 6 characters)
  const intToRGBA = (i: number): string => {
    const color =
      ((i >> 24) & 0xff).toString(16).padStart(2, '0') +
      ((i >> 16) & 0xff).toString(16).padStart(2, '0') +
      ((i >> 8) & 0xff).toString(16).padStart(2, '0');
    return color;
  };

  return shade(intToRGBA(hash(str)), prc);
}

interface IndividualPlanScreenState {
  // Individual Plan
  isLoadingIndividualPlan: boolean;
  isUpdatingIndividualPlan: boolean;
  individualPlan: IndividualPlan | null;

  // Individual Plan > Diagnostics
  isAddDiagnosticPromptOpen: boolean;

  // Individual Plan Cares
  individualPlanCares?: IndividualPlanCares | null;
}

const DEFINITION_MAX_CHARACTERS = 1000;

// Seconds until the AI can be used again after a 429 (the limit is per hour)
const getRetryAfterSeconds = (error: any): number => {
  const fromBody = Number(error?.response?.data?.retryAfterSeconds);
  const fromHeader = Number(error?.response?.headers?.['retry-after']);
  const seconds = [fromBody, fromHeader].find((value) => Number.isFinite(value) && value > 0);
  return seconds ?? 3600;
};
const CARE_MAX_CHARACTERS = 500;
const nonAutomaticCareChipSx = {
  height: 'auto',
  maxWidth: '100%',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
};
const CARES_MAX_COUNT = 20;

const normalizeText = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/**
 * Same matching as the default Autocomplete filter (case and accent insensitive, matches anywhere),
 * so the "add non-automatic care" action can be shown exactly when there are no options left.
 */
const filterActivityTypes = (options: Array<ActivityType>, inputValue: string) => {
  const search = normalizeText(inputValue);
  return options.filter((option) => normalizeText(option.name).includes(search));
};

/**
 * Builds the `cares` sent to the backend. Omitted keeps, `null` removes and a list replaces:
 * - Cares on the form: send the list (existing cares keep their id).
 * - No cares on the form but the objective had some: send `null` to remove them.
 * - Nothing before and nothing now: omit the field.
 */
const buildCaresPayload = (
  formCares: Array<IndividualPlanDiagnosticGoalObjectiveCare> | null | undefined,
  existingCares: Array<IndividualPlanDiagnosticGoalObjectiveCare> | null | undefined
): Array<IndividualPlanDiagnosticGoalObjectiveCare> | null | undefined => {
  if (formCares && formCares.length > 0) {
    return formCares.map((care) => ({id: care.id, definition: care.definition}));
  }
  if (existingCares && existingCares.length > 0) {
    return null;
  }
  return undefined;
};

const IndividualPlanDiagnosticObjectiveComponent = ({
  state,
  setState,
  individualPlanDiagnostic,
  individualPlanDiagnosticGoal,
  individualPlanDiagnosticGoalObjective,
  onEdit,
  onRemove,
  canWrite,
}: {
  state: IndividualPlanScreenState;
  setState: React.Dispatch<React.SetStateAction<IndividualPlanScreenState>>;
  individualPlanDiagnostic: IndividualPlanDiagnostic;
  individualPlanDiagnosticGoal: IndividualPlanDiagnosticGoal;
  individualPlanDiagnosticGoalObjective: IndividualPlanDiagnosticGoalObjective;
  onEdit: () => void;
  onRemove: () => void;
  canWrite: boolean;
}) => {
  const {t} = useTranslation();

  const [results, setResults] = useState<IndividualPlanResults | null>(null);
  const [loadingResults, setLoadingResults] = useState<boolean>(false);

  const ObjectiveBox = ({
    flex,
    width,
    noBackground,
    noPadding,
    children,
  }: {
    flex?: number;
    width?: string;
    noBackground?: boolean;
    noPadding?: boolean;
    children: React.ReactNode;
  }) => {
    return (
      <Box
        sx={{
          backgroundColor: noBackground ? 'transparent' : Pallete.gray.lighter,
          flex,
          width,
          paddingLeft: noPadding ? '0' : '16px',
          paddingRight: noPadding ? '0' : '16px',
          paddingTop: '8px',
          paddingBottom: '8px',
          borderRadius: '16px',
        }}
      >
        {children}
      </Box>
    );
  };

  useEffect(() => {
    if (
      individualPlanDiagnosticGoalObjective.activityTypesIds &&
      individualPlanDiagnosticGoalObjective.activityTypesIds.length > 0
    ) {
      // Load results
      setLoadingResults(true);
      IndividualPlansRepository.instance
        .getIndividualPlanDiagnosticGoalObjectiveResults(
          state.individualPlan!,
          individualPlanDiagnostic,
          individualPlanDiagnosticGoal,
          individualPlanDiagnosticGoalObjective
        )
        .then((data) => {
          setResults(data);
        })
        .catch((err) => {
          console.error(`Error: ${err}`);
          setResults(null);
        })
        .finally(() => {
          setLoadingResults(false);
        });
    }
  }, [
    individualPlanDiagnostic,
    individualPlanDiagnosticGoal,
    individualPlanDiagnosticGoalObjective,
    individualPlanDiagnosticGoalObjective.activityTypesIds,
    state.individualPlan,
  ]);

  // Calculate results percentages
  const totalCount =
    (results?.counts.missed ?? 0) +
    (results?.counts.notDone ?? 0) +
    (results?.counts.done ?? 0) +
    (results?.counts.planned ?? 0);
  const executedCount = (results?.counts.notDone ?? 0) + (results?.counts.done ?? 0);

  const missedPercentage = ((results?.counts.missed ?? 0) * 100) / totalCount;
  const notDonePercentage = ((results?.counts.notDone ?? 0) * 100) / totalCount;
  const donePercentage = ((results?.counts.done ?? 0) * 100) / totalCount;
  const plannedPercentage = ((results?.counts.planned ?? 0) * 100) / totalCount;
  const executedPercentage = notDonePercentage + donePercentage;

  // Build
  return (
    <Stack direction="row" gap="8px">
      <ObjectiveBox flex={3}>
        <Typography
          whiteSpace={'normal'}
          sx={{
            fontSize: '16px',
            lineHeight: '19px',
          }}
        >
          {individualPlanDiagnosticGoalObjective.definition}
        </Typography>
      </ObjectiveBox>
      <ObjectiveBox flex={3}>
        <Typography
          component="div"
          sx={{
            fontSize: '16px',
            lineHeight: '19px',
            width: {xs: '50px', md: 'auto'},
            marginRight: '10px',
          }}
        >
          <Stack gap="8px" direction="row" flexWrap="wrap" alignItems="center">
            {individualPlanDiagnosticGoalObjective.activityTypesIds?.map(
              (activityTypeId: string) => {
                return (
                  <Chip
                    key={activityTypeId}
                    sx={{
                      backgroundColor: `#${stringToColor(activityTypeId)}`,
                      color: 'white',
                    }}
                    label={
                      state.individualPlanCares?.activityTypes.find(
                        (option) => option.id === activityTypeId
                      )?.name
                    }
                  />
                );
              }
            )}
          </Stack>
          {!!individualPlanDiagnosticGoalObjective.activityTypesIds?.length &&
            !!individualPlanDiagnosticGoalObjective.cares?.length && (
              <Divider sx={{marginTop: '8px', marginBottom: '8px'}} />
            )}
          {!!individualPlanDiagnosticGoalObjective.cares?.length && (
            <Stack gap="8px" direction="row" flexWrap="wrap" alignItems="center">
              {individualPlanDiagnosticGoalObjective.cares.map((care, index) => (
                <Chip
                  key={care.id ?? index}
                  variant="outlined"
                  label={care.definition}
                  sx={nonAutomaticCareChipSx}
                />
              ))}
            </Stack>
          )}
        </Typography>
      </ObjectiveBox>
      {/* <ObjectiveBox flex={3}>
        <Typography
          sx={{
            fontSize: '16px',
            lineHeight: '19px',
            width: {xs: '50px', md: 'auto'},
            marginRight: '10px',
          }}
        >
          {individualPlanDiagnosticGoalObjective.definition}
        </Typography>
      </ObjectiveBox>
     <ObjectiveBox flex={2}>
        <Typography
          sx={{
            fontSize: '16px',
            lineHeight: '19px',
            width: {xs: '50px', md: 'auto'},
            marginRight: '10px',
          }}
        >
          {individualPlanDiagnosticGoalObjective.definition}
        </Typography>
      </ObjectiveBox>*/}
      <ObjectiveBox flex={2}>
        <Stack
          direction="row"
          alignItems="center"
          gap="8px"
          sx={{
            height: '100%',
          }}
        >
          <Box
            sx={{
              flex: 1,
            }}
          >
            <ProgressLine
              indeterminate={loadingResults}
              backgroundColor="lightgrey"
              visualParts={
                results
                  ? [
                      {
                        percentage: `${missedPercentage.toFixed(0)}%`,
                        color: Pallete.yellow.base,
                        title: `${t(
                          translations.care_and_activities.status_types[
                            CaregiverTaskCompletion.MISSED
                          ]
                        )} (${results.counts.missed})`,
                      },
                      {
                        percentage: `${notDonePercentage.toFixed(0)}%`,
                        color: Pallete.red.base,
                        title: `${t(
                          translations.care_and_activities.status_types[
                            CaregiverTaskCompletion.NOT_DONE
                          ]
                        )} (${results.counts.notDone})`,
                      },
                      {
                        percentage: `${donePercentage.toFixed(0)}%`,
                        color: Pallete.green.base,
                        title: `${t(
                          translations.care_and_activities.status_types[
                            CaregiverTaskCompletion.DONE
                          ]
                        )} (${results.counts.done})`,
                      },
                      {
                        percentage: `${plannedPercentage.toFixed(0)}%`,
                        color: Pallete.gray.light,
                        title: `${t(
                          translations.care_and_activities.status_types[
                            CaregiverTaskCompletion.PLANNED
                          ]
                        )} (${results.counts.planned})`,
                      },
                    ]
                  : []
              }
            />
          </Box>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '19px',
              width: '42px',
              textAlign: 'right',
            }}
          >
            {!loadingResults && (
              <>
                {results && totalCount > 0 ? (
                  <Tooltip title={`${executedCount}/${totalCount}`}>
                    <span>{executedPercentage.toFixed(0)}%</span>
                  </Tooltip>
                ) : (
                  <span>0%</span>
                )}
              </>
            )}
          </Typography>
        </Stack>
      </ObjectiveBox>
      {canWrite && (
        <ObjectiveBox width="32px" noBackground noPadding>
          <ComboActions
            triggerSX={{
              padding: '4px',
              pointerEvents: 'auto',
            }}
            actions={[
              {
                label: t(translations.generic_header.actions.edit),
                icon: {element: FiEdit},
                callback: () => {
                  onEdit();
                },
                disabled: state.isUpdatingIndividualPlan,
              },
              {
                label: t(translations.modal.delete),
                icon: {element: FiTrash2},
                callback: () => {
                  onRemove();
                },
                disabled: state.isUpdatingIndividualPlan,
              },
            ]}
          />
        </ObjectiveBox>
      )}
    </Stack>
  );
};

function CareOption(props: {
  htmlProps: React.HTMLAttributes<HTMLElement>;
  value: ActivityType;
  selected: boolean;
}) {
  return (
    <Stack
      {...props.htmlProps}
      flexDirection="row"
      height="38px"
      padding="0 6px !important"
      gap="8px"
    >
      <Checkbox size="small" checked={props.selected} sx={{width: '12px', height: '12px'}} />
      <span>{props.value.name}</span>
    </Stack>
  );
}

function CarePickerInput(props: {params: AutocompleteRenderInputParams; placeholder?: string}) {
  return (
    <TextField
      {...props.params}
      variant="filled"
      placeholder={props.placeholder}
      InputProps={{
        ...props.params.InputProps,
      }}
      sx={{
        '& .MuiInputBase-root': {
          height: '48px',
          padding: '8px 12px',
          alignContent: 'center',
          gap: '6px',
        },
        '& .MuiInputBase-input': {
          color: 'gray.main',
          fontSize: '14px',
        },
        '& .MuiInputAdornment-root': {
          margin: '0 !important',
          height: '100%',
        },
      }}
    />
  );
}

const IndividualPlanDiagnosticGoalComponent = ({
  state,
  setState,
  individualPlanDiagnostic,
  individualPlanDiagnosticGoal,
  onEdit,
  onRemove,
  canWrite,
}: {
  state: IndividualPlanScreenState;
  setState: React.Dispatch<React.SetStateAction<IndividualPlanScreenState>>;
  individualPlanDiagnostic: IndividualPlanDiagnostic;
  individualPlanDiagnosticGoal: IndividualPlanDiagnosticGoal;
  onEdit: () => void;
  onRemove: () => void;
  canWrite: boolean;
}) => {
  interface IndividualPlanDiagnosticGoalObjectiveFormValidation {
    definition?: {
      errorMessage: string | undefined;
    };
  }

  const {isRunningMobile} = useObserveViewport();
  const {t} = useTranslation();
  const snackBarContext = useSnackBar();
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [careInputValue, setCareInputValue] = useState<string>('');
  const [careInputError, setCareInputError] = useState<string | undefined>(undefined);
  const [viewResponsibleUser, setViewResponsibleUser] = useState<UserPublicInfo | null>(null);
  const [loadingViewResponsibleUser, setLoadingViewResponsibleUser] = useState<boolean>(true);
  const [isAddDiagnosticGoalObjectivePromptOpen, setIsAddDiagnosticGoalObjectivePromptOpen] =
    useState<boolean>(false);
  const [model, setModel] = useState<IndividualPlanDiagnosticGoalObjective>({
    definition: '',
  });
  const [formValidation, setFormValidation] =
    useState<IndividualPlanDiagnosticGoalObjectiveFormValidation>({
      definition: {
        errorMessage: undefined,
      },
    });
  const [isRemovingPromptOpen, setIsRemovingPromptOpen] = useState<boolean>(false);

  // Must keep the same reference between renders: the Autocomplete clears the typed text whenever
  // its `value` changes
  const selectedActivityTypes = useMemo(
    () =>
      model.activityTypesIds
        ? (state.individualPlanCares?.activityTypes.filter((option) =>
            model.activityTypesIds?.includes(option.id ?? '')
          ) ?? [])
        : [],
    [model.activityTypesIds, state.individualPlanCares]
  );

  const clearModelAndForm = useCallback((useModel?: IndividualPlanDiagnosticGoalObjective) => {
    setModel(
      useModel || {
        definition: '',
      }
    );
    setFormValidation({
      definition: {
        errorMessage: undefined,
      },
    });
    setCareInputValue('');
    setCareInputError(undefined);
  }, []);

  function addNonAutomaticCare(text: string) {
    const definition = text.trim();
    if (definition === '') {
      return;
    }

    if (definition.length > CARE_MAX_CHARACTERS) {
      setCareInputError(
        t(
          translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal_objective.cares
            .too_long,
          {
            max: CARE_MAX_CHARACTERS,
          }
        )
      );
      return;
    }

    const currentCares = model.cares ?? [];
    if (currentCares.some((care) => normalizeText(care.definition) === normalizeText(definition))) {
      // Already on the list
      setCareInputValue('');
      setCareInputError(undefined);
      return;
    }

    if (currentCares.length >= CARES_MAX_COUNT) {
      setCareInputError(
        t(
          translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal_objective.cares
            .max_reached,
          {
            max: CARES_MAX_COUNT,
          }
        )
      );
      return;
    }

    setModel({...model, cares: [...currentCares, {definition}]});
    setCareInputValue('');
    setCareInputError(undefined);
  }

  async function addObjective() {
    // Validate Form
    let goalFormValidation: IndividualPlanDiagnosticGoalObjectiveFormValidation = {};
    if (
      !model.definition ||
      model.definition.trim() === '' ||
      model.definition.length > DEFINITION_MAX_CHARACTERS
    ) {
      goalFormValidation = {
        ...goalFormValidation,
        definition: {
          errorMessage: t(
            translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal.form.definition
              .error_message,
            {
              max: DEFINITION_MAX_CHARACTERS,
            }
          ),
        },
      };
    }
    if (Object.keys(goalFormValidation).length > 0) {
      setFormValidation(goalFormValidation);
      return;
    }

    // Start loading
    setState((prevState) => ({
      ...prevState,
      isUpdatingIndividualPlan: true,
    }));

    // Cares: omitted keeps, null removes, a list replaces
    const cares = buildCaresPayload(
      model.cares,
      model.id
        ? individualPlanDiagnosticGoal.objectives?.find((objective) => objective.id === model.id)
            ?.cares
        : undefined
    );

    // Do it
    const promise = model?.id
      ? IndividualPlansRepository.instance.updateIndividualPlanDiagnosticGoalObjective(
          state.individualPlan!,
          individualPlanDiagnostic,
          individualPlanDiagnosticGoal,
          {
            ...model,
            cares,
            indicators: !model.indicators ? null : model.indicators,
            activityTypesIds: !model.activityTypesIds ? null : model.activityTypesIds,
          }
        )
      : IndividualPlansRepository.instance.createIndividualPlanDiagnosticGoalObjective(
          state.individualPlan!,
          individualPlanDiagnostic,
          individualPlanDiagnosticGoal,
          {...model, cares}
        );
    // Do it
    promise
      .then((data) => {
        setState((prevState) => ({
          ...prevState,
          individualPlan: data,
          isUpdatingIndividualPlan: false,
        }));
        setIsAddDiagnosticGoalObjectivePromptOpen(false);
        clearModelAndForm();
      })
      .catch((err) => {
        console.error(`Error: ${err}`);
        setState((prevState) => ({
          ...prevState,
          isUpdatingIndividualPlan: false,
        }));

        // Keep the modal open so nothing typed is lost; a 400 means an id is not usable
        const messages =
          translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal_objective
            .snackbar;
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(err?.response?.status === 400 ? messages.invalid_references : messages.error),
        });
      });
  }

  function removeObjective() {
    // Start loading
    setState((prevState) => ({
      ...prevState,
      isUpdatingIndividualPlan: true,
    }));

    // Do it
    IndividualPlansRepository.instance
      .removeIndividualPlanDiagnosticGoalObjective(
        state.individualPlan!,
        individualPlanDiagnostic,
        individualPlanDiagnosticGoal,
        model
      )
      .finally(() => {
        // Close prompt and stop loading
        setIsRemovingPromptOpen(false);
        clearModelAndForm();

        // Diagnostics list
        setState((prevState) => {
          const currentDiagnosticsList = [...prevState.individualPlan!.diagnostics!];
          const diagnosticIndex = currentDiagnosticsList.findIndex(
            (diagnostic) => diagnostic.id === individualPlanDiagnostic.id
          );
          const goalIndex = currentDiagnosticsList[diagnosticIndex].goals?.findIndex(
            (goal) => goal.id === individualPlanDiagnosticGoal.id
          );
          if (
            diagnosticIndex !== undefined &&
            diagnosticIndex >= 0 &&
            goalIndex !== undefined &&
            goalIndex >= 0
          ) {
            // Locate the objective and remove it
            const newObjectivesList = currentDiagnosticsList[diagnosticIndex].goals![
              goalIndex
            ].objectives?.filter((objective) => objective.id !== model.id);
            currentDiagnosticsList![diagnosticIndex].goals![goalIndex].objectives =
              newObjectivesList;
          }

          return {
            ...prevState,
            isUpdatingIndividualPlan: false,
            individualPlan: {
              ...prevState.individualPlan!,
              diagnostics: currentDiagnosticsList,
            },
          };
        });
      });
  }

  useEffect(() => {
    if (individualPlanDiagnosticGoal.responsibleUserId) {
      // Load user
      UsersRepository.instance
        .getUserPublicInfo(individualPlanDiagnosticGoal.responsibleUserId)
        .then((user) => {
          setViewResponsibleUser(user);
        })
        .finally(() => {
          setLoadingViewResponsibleUser(false);
        });
    } else {
      setViewResponsibleUser(null);
      setLoadingViewResponsibleUser(false);
    }
  }, [individualPlanDiagnosticGoal.responsibleUserId, state, viewResponsibleUser]);

  return (
    <>
      <Accordion
        elevation={0}
        sx={{
          marginBottom: '16px',
          '&:before': {
            display: 'none',
          },
        }}
        expanded={isExpanded}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon onClick={() => setIsExpanded(!isExpanded)} />}
          sx={{
            backgroundColor: Pallete.gray.lighter,
            borderRadius: '5px',
            height: '56px',
            minHeight: '56px !important',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            borderBottomLeftRadius: '0',
            borderBottomRightRadius: '0',
            boxShadow: '0px -2px 4px 0px rgba(0, 0, 0, 0.10)',
          }}
        >
          <Box
            sx={{
              flex: 4,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '19px',
                width: {xs: '50px', md: 'auto'},
                marginRight: '10px',
              }}
              noWrap
            >
              {t(translations.seniors.edit.senior_dcm.individual_plan.goal)}:
            </Typography>
            <Typography
              whiteSpace="normal"
              sx={{
                fontSize: '16px',
                lineHeight: '19px',
                width: {xs: '50px', md: 'auto'},
                marginRight: '10px',
              }}
            >
              {individualPlanDiagnosticGoal.definition}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '19px',
                width: {xs: '50px', md: 'auto'},
                marginRight: '10px',
              }}
              noWrap
            >
              {t(
                translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal.form
                  .responsibleUser.label
              )}
              :
            </Typography>
            {loadingViewResponsibleUser && <Skeleton width={234} height={32} />}
            {!loadingViewResponsibleUser && (
              <Stack
                flexDirection="row"
                gap="6px"
                alignItems="center"
                sx={{
                  cursor: 'not-allowed',
                }}
              >
                {viewResponsibleUser && (
                  <Avatar
                    sx={{height: '28px', width: '28px', border: '1px solid white'}}
                    src={viewResponsibleUser.picture}
                    alt={`avatar_${viewResponsibleUser?.name}`}
                  />
                )}
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '24px',
                  }}
                >
                  {viewResponsibleUser
                    ? viewResponsibleUser?.name
                    : t(translations.seniors.edit.senior_dcm.individual_plan.caregiver.unassigned)}
                </Typography>
              </Stack>
            )}
          </Box>
          {canWrite && (
            <ComboActions
              triggerSX={{
                padding: '4px',
                marginRight: '8px',
                pointerEvents: 'auto',
              }}
              actions={[
                {
                  label: t(translations.generic_header.actions.edit),
                  icon: {element: FiEdit},
                  callback: () => {
                    onEdit();
                  },
                  disabled: state.isUpdatingIndividualPlan,
                },
                {
                  label: t(translations.modal.delete),
                  icon: {element: FiTrash2},
                  callback: () => {
                    onRemove();
                  },
                  disabled: state.isUpdatingIndividualPlan,
                },
              ]}
            />
          )}
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingTop: '16px',
            paddingBottom: '16px',
            paddingLeft: '8px',
            paddingRight: '8px',
            borderBottom: `1px solid ${Pallete.gray.lightbg}`,
          }}
        >
          {individualPlanDiagnosticGoal.objectives &&
            individualPlanDiagnosticGoal.objectives.length > 0 && (
              <Stack
                gap="8px"
                sx={{
                  marginBottom: '16px',
                }}
              >
                <Stack direction="row" gap="8px">
                  <Box flex={3}>
                    <Typography
                      textAlign="center"
                      fontWeight={600}
                      fontSize="16px"
                      lineHeight="19px"
                      noWrap
                    >
                      {t(
                        translations.seniors.edit.senior_dcm.individual_plan
                          .add_diagnostic_goal_objective.table.definition
                      )}
                    </Typography>
                  </Box>
                  <Box flex={3}>
                    <Typography
                      textAlign="center"
                      fontWeight={600}
                      fontSize="16px"
                      lineHeight="19px"
                      noWrap
                    >
                      {t(
                        translations.seniors.edit.senior_dcm.individual_plan
                          .add_diagnostic_goal_objective.table.cares
                      )}
                    </Typography>
                  </Box>
                  {/* <Box flex={3}>
                    <Typography
                      textAlign="center"
                      fontWeight={600}
                      fontSize="16px"
                      lineHeight="19px"
                      noWrap
                    >
                      {t(
                        translations.seniors.edit.senior_dcm.individual_plan
                          .add_diagnostic_goal_objective.table.indicators
                      )}
                    </Typography>
                  </Box>
                  <Box flex={2}>
                    <Typography
                      textAlign="center"
                      fontWeight={600}
                      fontSize="16px"
                      lineHeight="19px"
                      noWrap
                    >
                      {t(
                        translations.seniors.edit.senior_dcm.individual_plan
                          .add_diagnostic_goal_objective.table.targets
                      )}
                    </Typography>
                  </Box> */}
                  <Box flex={2}>
                    <Typography
                      textAlign="center"
                      fontWeight={600}
                      fontSize="16px"
                      lineHeight="19px"
                      noWrap
                    >
                      {t(
                        translations.seniors.edit.senior_dcm.individual_plan
                          .add_diagnostic_goal_objective.table.execution
                      )}
                    </Typography>
                  </Box>
                  {canWrite && (
                    <Box
                      sx={{
                        width: '32px',
                      }}
                    ></Box>
                  )}
                </Stack>
                {individualPlanDiagnosticGoal.objectives?.map((objective, index) => {
                  return (
                    <IndividualPlanDiagnosticObjectiveComponent
                      key={index}
                      state={state}
                      setState={setState}
                      individualPlanDiagnostic={individualPlanDiagnostic}
                      individualPlanDiagnosticGoal={individualPlanDiagnosticGoal}
                      individualPlanDiagnosticGoalObjective={objective}
                      onEdit={() => {
                        // Clear
                        clearModelAndForm(objective);

                        // Open
                        setIsAddDiagnosticGoalObjectivePromptOpen(true);
                      }}
                      onRemove={() => {
                        // Clear
                        clearModelAndForm(objective);

                        // Open
                        setIsRemovingPromptOpen(true);
                      }}
                      canWrite={canWrite}
                    />
                  );
                })}
              </Stack>
            )}
          {canWrite && (
            <CardActions
              disableSpacing
              sx={{
                padding: '0',
              }}
            >
              <Stack
                direction="row"
                justifyContent="center"
                sx={{
                  width: '100%',
                }}
              >
                <Button
                  onClick={() => {
                    // Open
                    setIsAddDiagnosticGoalObjectivePromptOpen(true);
                  }}
                  variant="inverted"
                  size="small"
                  startIcon={<Add />}
                  disabled={state.isUpdatingIndividualPlan}
                  sx={{
                    width: 'auto',
                    height: '40px',
                    letterSpacing: '0.2px',
                    py: '8px',
                    '.MuiButton-startIcon': {
                      margin: isRunningMobile ? 0 : '0 8px 0 -4px',
                    },
                    border: `1px solid ${Pallete.blue.base}`,
                  }}
                >
                  {!isRunningMobile &&
                    t(translations.seniors.edit.senior_dcm.individual_plan.new_objective)}
                </Button>
              </Stack>
            </CardActions>
          )}
        </AccordionDetails>
      </Accordion>
      {isAddDiagnosticGoalObjectivePromptOpen && (
        <Modal
          open={isAddDiagnosticGoalObjectivePromptOpen}
          onClose={() => {
            setIsAddDiagnosticGoalObjectivePromptOpen(false);
            clearModelAndForm();
          }}
          maxWidth="sm"
          fullWidth
          preventCloseClick
          title={
            model?.id
              ? t(
                  translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal_objective
                    .edit
                )
              : t(
                  translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal_objective
                    .title
                )
          }
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: () => {
                setIsAddDiagnosticGoalObjectivePromptOpen(false);
                clearModelAndForm();
              },
              disabled: state.isUpdatingIndividualPlan,
            },
            {
              label: individualPlanDiagnosticGoal?.id
                ? t(translations.modal.save)
                : t(translations.modal.add),
              type: 'nondestructive',
              onClick: addObjective,
              loading: state.isUpdatingIndividualPlan,
            },
          ]}
        >
          <form>
            <FlatInput
              disabled={state.isUpdatingIndividualPlan}
              value={model.definition}
              label={t(
                translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal_objective
                  .form.definition.label
              )}
              placeholder={t(
                translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal_objective
                  .form.definition.placeholder
              )}
              errors={formValidation?.definition?.errorMessage}
              counter={{
                current: model.definition?.length ?? 0,
                max: DEFINITION_MAX_CHARACTERS,
              }}
              required
              onChange={(event) => {
                clearModelAndForm({
                  ...model,
                  definition: event.target.value,
                });
              }}
            />
            <Box
              sx={{
                marginTop: '30px',
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontWeight: 500,
                  fontSize: '16px',
                  color: 'gray.main',
                  marginBottom: '4px',
                }}
              >
                {t(
                  translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal_objective
                    .table.cares
                )}
              </Typography>
              <Autocomplete
                sx={{
                  '.MuiInputBase-root': {
                    height: 'auto',
                    paddingTop: '8px',
                  },
                }}
                disabled={state.isUpdatingIndividualPlan}
                multiple
                disableCloseOnSelect
                inputValue={careInputValue}
                onInputChange={(_, value, reason) => {
                  setCareInputValue(reason === 'reset' ? '' : value);
                  setCareInputError(undefined);
                }}
                filterOptions={(options, params) => filterActivityTypes(options, params.inputValue)}
                onKeyDown={(event) => {
                  // Enter with text that matches no activity type adds a non-automatic care
                  if (event.key !== 'Enter' || careInputValue.trim() === '') {
                    return;
                  }
                  const options = state.individualPlanCares?.activityTypes ?? [];
                  if (filterActivityTypes(options, careInputValue).length === 0) {
                    event.preventDefault();
                    addNonAutomaticCare(careInputValue);
                  }
                }}
                options={state.individualPlanCares ? state.individualPlanCares.activityTypes : []}
                groupBy={(option) => {
                  let groupId = option.activitiesGroupId;
                  if (state.individualPlanCares?.activitiesGroups) {
                    const group = state.individualPlanCares.activitiesGroups.find(
                      (group) => group.id === groupId
                    );
                    if (group) {
                      let sectorId = group.activitiesSectorId;
                      const sector = state.individualPlanCares?.activitiesSectors.find(
                        (sector) => sector.id === sectorId
                      );
                      return `${sector?.name} > ${group?.name}`;
                    }
                  }
                  return '';
                }}
                value={selectedActivityTypes}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <CarePickerInput
                    params={params}
                    placeholder={t(translations.seniors.edit.senior_dcm.individual_plan.add_cares)}
                  />
                )}
                // loading={loadingCaresList}
                loadingText={t(translations.loading_content.loading)}
                noOptionsText={
                  careInputValue.trim() === '' ? (
                    t(translations.search_select_input.no_options)
                  ) : (
                    <Button
                      fullWidth
                      startIcon={<Add />}
                      sx={{justifyContent: 'flex-start', textTransform: 'none'}}
                      onClick={() => addNonAutomaticCare(careInputValue)}
                    >
                      {t(
                        translations.seniors.edit.senior_dcm.individual_plan
                          .add_diagnostic_goal_objective.cares.add_non_automatic
                      )}
                    </Button>
                  )
                }
                renderOption={(props, option, state) => (
                  <CareOption
                    key={option.id}
                    htmlProps={props}
                    value={option}
                    selected={state.selected}
                  />
                )}
                disableClearable
                onChange={(_, values, reason, details) => {
                  if (reason === 'selectOption' || reason === 'removeOption') {
                    setModel({
                      ...model,
                      activityTypesIds: values
                        .map((item) => item.id)
                        .filter((id): id is string => id !== undefined),
                    });
                  }
                }}
                renderTags={(value: ActivityType[], getTagProps) =>
                  value.map((option: ActivityType, index) => {
                    return (
                      <Chip
                        sx={{
                          backgroundColor: `#${stringToColor(option.id ?? '')}`,
                          color: 'white',
                        }}
                        label={option.name}
                        {...getTagProps({index})}
                      />
                    );
                  })
                }
              />
              {careInputError && (
                <Typography
                  sx={{
                    color: 'error.main',
                    fontSize: '12px',
                    marginTop: '4px',
                  }}
                >
                  {careInputError}
                </Typography>
              )}
              {!!model.cares?.length && (
                <Box
                  sx={{
                    marginTop: '16px',
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: 500,
                      fontSize: '16px',
                      color: 'gray.main',
                      marginBottom: '4px',
                    }}
                  >
                    {t(
                      translations.seniors.edit.senior_dcm.individual_plan
                        .add_diagnostic_goal_objective.cares.non_automatic_title
                    )}
                  </Typography>
                  <Stack gap="8px" direction="row" flexWrap="wrap" alignItems="center">
                    {model.cares.map((care, index) => (
                      <Chip
                        key={care.id ?? `new-${index}`}
                        variant="outlined"
                        label={care.definition}
                        sx={nonAutomaticCareChipSx}
                        disabled={state.isUpdatingIndividualPlan}
                        onDelete={() => {
                          setModel({
                            ...model,
                            cares: model.cares?.filter((_, careIndex) => careIndex !== index),
                          });
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </form>
        </Modal>
      )}
      {isRemovingPromptOpen && (
        <Modal
          open={isRemovingPromptOpen}
          onClose={() => {
            setIsRemovingPromptOpen(false);
            clearModelAndForm();
          }}
          maxWidth="sm"
          fullWidth
          className="delete-modal"
          preventCloseClick
          title={t(
            translations.seniors.edit.senior_dcm.individual_plan.on_diagnostic_goal_objective_remove
              .header
          )}
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: () => {
                setIsRemovingPromptOpen(false);
                clearModelAndForm();
              },
              disabled: state.isUpdatingIndividualPlan,
            },
            {
              label: t(translations.modal.delete),
              type: 'nondestructive',
              onClick: removeObjective,
              loading: state.isUpdatingIndividualPlan,
            },
          ]}
        >
          <Stack className="container">
            <Typography className="title">
              {t(
                translations.seniors.edit.senior_dcm.individual_plan
                  .on_diagnostic_goal_objective_remove.title,
                {
                  name: model.definition,
                }
              )}
            </Typography>
            <Typography className="subtitle">
              {t(
                translations.seniors.edit.senior_dcm.individual_plan
                  .on_diagnostic_goal_objective_remove.description
              )}
            </Typography>
          </Stack>
        </Modal>
      )}
    </>
  );
};

const IndividualPlanDiagnosticComponent = ({
  state,
  setState,
  individualPlanDiagnostic,
  onEdit,
  onRemove,
  canWrite,
}: {
  state: IndividualPlanScreenState;
  setState: React.Dispatch<React.SetStateAction<IndividualPlanScreenState>>;
  individualPlanDiagnostic: IndividualPlanDiagnostic;
  onEdit: () => void;
  onRemove: () => void;
  canWrite: boolean;
}) => {
  interface IndividualPlanDiagnosticGoalFormValidation {
    definition?: {
      errorMessage: string | undefined;
    };
  }

  const {t} = useTranslation();
  const {isRunningMobile} = useObserveViewport();
  const {items: caregivers, isLoading: isLoadingCaregivers} = useFetchEntityUsers({
    userType: UserType.Caregiver,
    activeUsers: true,
  });
  const [isAddDiagnosticGoalPromptOpen, setIsAddDiagnosticGoalPromptOpen] =
    useState<boolean>(false);
  const [model, setModel] = useState<IndividualPlanDiagnosticGoal>({
    definition: '',
  });
  const [formValidation, setFormValidation] = useState<IndividualPlanDiagnosticGoalFormValidation>({
    definition: {
      errorMessage: undefined,
    },
  });
  const [isRemovingPromptOpen, setIsRemovingPromptOpen] = useState<boolean>(false);

  const clearModelAndForm = useCallback((useModel?: IndividualPlanDiagnosticGoal) => {
    setModel(
      useModel || {
        definition: '',
      }
    );
    setFormValidation({
      definition: {
        errorMessage: undefined,
      },
    });
  }, []);

  async function addGoal() {
    // Validate Form
    let goalFormValidation: IndividualPlanDiagnosticGoalFormValidation = {};
    if (
      !model.definition ||
      model.definition.trim() === '' ||
      model.definition.length > DEFINITION_MAX_CHARACTERS
    ) {
      goalFormValidation = {
        ...goalFormValidation,
        definition: {
          errorMessage: t(
            translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal.form.definition
              .error_message,
            {
              max: DEFINITION_MAX_CHARACTERS,
            }
          ),
        },
      };
    }

    if (Object.keys(goalFormValidation).length > 0) {
      setFormValidation(goalFormValidation);
      return;
    }

    // Start loading
    setState((prevState) => ({
      ...prevState,
      isUpdatingIndividualPlan: true,
    }));

    // Do it
    const promise = model?.id
      ? IndividualPlansRepository.instance.updateIndividualPlanDiagnosticGoal(
          state.individualPlan!,
          individualPlanDiagnostic,
          model
        )
      : IndividualPlansRepository.instance.createIndividualPlanDiagnosticGoal(
          state.individualPlan!,
          individualPlanDiagnostic,
          model
        );

    // Do it
    promise
      .then((data) => {
        setState((prevState) => ({
          ...prevState,
          individualPlan: data,
          isUpdatingIndividualPlan: false,
        }));
        setIsAddDiagnosticGoalPromptOpen(false);
        clearModelAndForm();
      })
      .catch((err) => {
        console.error(`Error: ${err}`);
        setState((prevState) => ({
          ...prevState,
          isUpdatingIndividualPlan: false,
        }));
        setIsAddDiagnosticGoalPromptOpen(false);
        clearModelAndForm();
      });
  }

  function removeGoal() {
    // Start loading
    setState((prevState) => ({
      ...prevState,
      isUpdatingIndividualPlan: true,
    }));

    // Do it
    IndividualPlansRepository.instance
      .removeIndividualPlanDiagnosticGoal(state.individualPlan!, individualPlanDiagnostic, model)
      .finally(() => {
        // Close prompt and stop loading
        setIsRemovingPromptOpen(false);
        clearModelAndForm();

        // Diagnostics list
        setState((prevState) => {
          const currentDiagnosticsList = [...prevState.individualPlan!.diagnostics!];
          const diagnosticIndex = currentDiagnosticsList.findIndex(
            (diagnostic) => diagnostic.id === individualPlanDiagnostic.id
          );
          if (diagnosticIndex !== undefined && diagnosticIndex >= 0) {
            // Locate the goal and remove it
            const newGoalsList = currentDiagnosticsList[diagnosticIndex].goals?.filter(
              (goal) => goal.id !== model.id
            );
            currentDiagnosticsList![diagnosticIndex].goals = newGoalsList;
          }

          return {
            ...prevState,
            isUpdatingIndividualPlan: false,
            individualPlan: {
              ...prevState.individualPlan!,
              diagnostics: currentDiagnosticsList,
            },
          };
        });
      });
  }

  // Pick selected caregiver
  const selectedAffiliation = caregivers?.find(
    (caregiver) => caregiver.user.id === model.responsibleUserId
  );

  return (
    <>
      <Stack
        sx={{
          marginBottom: '24px',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            paddingLeft: '8px',
            paddingRight: '8px',
            paddingBottom: '8px',
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t(translations.seniors.edit.senior_dcm.individual_plan.diagnostic)}:
          </Typography>
          <Typography
            variant="h6"
            sx={{
              marginLeft: '8px',
            }}
          >
            {individualPlanDiagnostic.definition}
          </Typography>
          <Box
            sx={{
              flex: 1,
            }}
          ></Box>
          {canWrite && (
            <ComboActions
              actions={[
                {
                  label: t(translations.generic_header.actions.edit),
                  icon: {element: FiEdit},
                  callback: () => {
                    onEdit();
                  },
                  disabled: state.isUpdatingIndividualPlan,
                },
                {
                  label: t(translations.modal.delete),
                  icon: {element: FiTrash2},
                  callback: () => {
                    onRemove();
                  },
                  disabled: state.isUpdatingIndividualPlan,
                },
              ]}
            />
          )}
        </Stack>
        <Card
          sx={{
            padding: '16px',
          }}
        >
          {individualPlanDiagnostic.goals?.map((goal, index) => {
            return (
              <IndividualPlanDiagnosticGoalComponent
                key={index}
                state={state}
                setState={setState}
                individualPlanDiagnostic={individualPlanDiagnostic}
                individualPlanDiagnosticGoal={goal}
                onEdit={() => {
                  // Clear
                  clearModelAndForm(goal);

                  // Open
                  setIsAddDiagnosticGoalPromptOpen(true);
                }}
                onRemove={() => {
                  // Clear
                  clearModelAndForm(goal);

                  // Open
                  setIsRemovingPromptOpen(true);
                }}
                canWrite={canWrite}
              />
            );
          })}
          {canWrite && (
            <CardActions
              disableSpacing
              sx={{
                padding: '0',
              }}
            >
              <Stack
                direction="row"
                justifyContent="center"
                sx={{
                  width: '100%',
                }}
              >
                <Button
                  onClick={() => {
                    // Open
                    setIsAddDiagnosticGoalPromptOpen(true);
                  }}
                  variant="inverted"
                  size="small"
                  startIcon={<Add />}
                  disabled={state.isUpdatingIndividualPlan}
                  sx={{
                    width: 'auto',
                    height: '40px',
                    letterSpacing: '0.2px',
                    py: '8px',
                    '.MuiButton-startIcon': {
                      margin: isRunningMobile ? 0 : '0 8px 0 -4px',
                    },
                    border: `1px solid ${Pallete.blue.base}`,
                  }}
                >
                  {!isRunningMobile &&
                    t(translations.seniors.edit.senior_dcm.individual_plan.new_goal)}
                </Button>
              </Stack>
            </CardActions>
          )}
        </Card>
      </Stack>
      {isAddDiagnosticGoalPromptOpen && (
        <Modal
          open={isAddDiagnosticGoalPromptOpen}
          onClose={() => {
            setIsAddDiagnosticGoalPromptOpen(false);
            clearModelAndForm();
          }}
          maxWidth="sm"
          fullWidth
          preventCloseClick
          title={
            model?.id
              ? t(translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal.edit)
              : t(translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal.title)
          }
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: () => {
                setIsAddDiagnosticGoalPromptOpen(false);
                clearModelAndForm();
              },
              disabled: state.isUpdatingIndividualPlan,
            },
            {
              label: individualPlanDiagnostic?.id
                ? t(translations.modal.save)
                : t(translations.modal.add),
              type: 'nondestructive',
              onClick: addGoal,
              loading: state.isUpdatingIndividualPlan || isLoadingCaregivers,
            },
          ]}
        >
          <form>
            <FlatInput
              disabled={state.isUpdatingIndividualPlan}
              value={model.definition}
              label={t(
                translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal.form
                  .definition.label
              )}
              placeholder={t(
                translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal.form
                  .definition.placeholder
              )}
              errors={formValidation?.definition?.errorMessage}
              counter={{
                current: model.definition?.length ?? 0,
                max: DEFINITION_MAX_CHARACTERS,
              }}
              required
              onChange={(event) => {
                clearModelAndForm({
                  ...model,
                  definition: event.target.value,
                });
              }}
            />
            <Box
              sx={{
                marginTop: '30px',
              }}
            >
              <FlatSelectDropdown
                disabled={state.isUpdatingIndividualPlan}
                label={t(
                  translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal.form
                    .responsibleUser.label
                )}
                placeholder={t(
                  translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic_goal.form
                    .responsibleUser.placeholder
                )}
                role="user"
                disabledTooltip={
                  !canWrite ? t(translations.device_form.input_disabled_tooltip) : undefined
                }
                value={
                  selectedAffiliation
                    ? {
                        key: selectedAffiliation.user.id!,
                        label:
                          selectedAffiliation.user.displayName || selectedAffiliation.user.name,
                        picture: selectedAffiliation.user.picture,
                      }
                    : undefined
                }
                loading={isLoadingCaregivers}
                options={
                  caregivers
                    ? caregivers.map((affiliationUser) => {
                        return {
                          key: affiliationUser.user.id!,
                          label: affiliationUser.user.displayName || affiliationUser.user.name,
                          picture: affiliationUser.user.picture,
                        };
                      })
                    : []
                }
                clearable
                onChange={(value) => {
                  if (!value.key) {
                    setModel({
                      ...model,
                      responsibleUserId: null,
                    });
                  } else {
                    setModel({
                      ...model,
                      responsibleUserId: value.key,
                    });
                  }
                }}
              />
            </Box>
          </form>
        </Modal>
      )}
      {isRemovingPromptOpen && (
        <Modal
          open={isRemovingPromptOpen}
          onClose={() => {
            setIsRemovingPromptOpen(false);
            clearModelAndForm();
          }}
          maxWidth="sm"
          fullWidth
          className="delete-modal"
          preventCloseClick
          title={t(
            translations.seniors.edit.senior_dcm.individual_plan.on_diagnostic_goal_remove.header
          )}
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: () => {
                setIsRemovingPromptOpen(false);
                clearModelAndForm();
              },
              disabled: state.isUpdatingIndividualPlan,
            },
            {
              label: t(translations.modal.delete),
              type: 'nondestructive',
              onClick: removeGoal,
              loading: state.isUpdatingIndividualPlan,
            },
          ]}
        >
          <Stack className="container">
            <Typography className="title">
              {t(
                translations.seniors.edit.senior_dcm.individual_plan.on_diagnostic_goal_remove
                  .title,
                {
                  name: model.definition,
                }
              )}
            </Typography>
            <Typography className="subtitle">
              {t(
                translations.seniors.edit.senior_dcm.individual_plan.on_diagnostic_goal_remove
                  .description
              )}
            </Typography>
          </Stack>
        </Modal>
      )}
    </>
  );
};

const IndividualPlanComponent = ({
  state,
  setState,
  saveChanges,
  canWrite,
}: {
  state: IndividualPlanScreenState;
  setState: React.Dispatch<React.SetStateAction<IndividualPlanScreenState>>;
  saveChanges: (changedIndividualPlan?: IndividualPlan) => Promise<IndividualPlan>;
  canWrite: boolean;
}) => {
  interface IndividualPlanDiagnosticFormValidation {
    definition?: {
      errorMessage: string | undefined;
    };
  }

  const {t} = useTranslation();
  const [model, setModel] = useState<IndividualPlanDiagnostic>({
    definition: '',
  });
  const [formValidation, setFormValidation] = useState<IndividualPlanDiagnosticFormValidation>({
    definition: {
      errorMessage: undefined,
    },
  });
  const [isRemovingPromptOpen, setIsRemovingPromptOpen] = useState<boolean>(false);

  const clearModelAndForm = useCallback((useModel?: IndividualPlanDiagnostic) => {
    setModel(
      useModel || {
        definition: '',
      }
    );
    setFormValidation({
      definition: {
        errorMessage: undefined,
      },
    });
  }, []);

  async function addDiagnostic() {
    // Validate Form
    let diagnosticFormValidation: IndividualPlanDiagnosticFormValidation = {};
    if (
      !model.definition ||
      model.definition.trim() === '' ||
      model.definition.length > DEFINITION_MAX_CHARACTERS
    ) {
      diagnosticFormValidation = {
        ...diagnosticFormValidation,
        definition: {
          errorMessage: t(
            translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic.form.definition
              .error_message,
            {
              max: DEFINITION_MAX_CHARACTERS,
            }
          ),
        },
      };
    }

    if (Object.keys(diagnosticFormValidation).length > 0) {
      setFormValidation(diagnosticFormValidation);
      return;
    }

    // Start loading
    setState((prevState) => ({
      ...prevState,
      isUpdatingIndividualPlan: true,
    }));

    // Get Individual Plan Id
    let individualPlan = state.individualPlan;

    // Check if Individual Plan was already created
    if (!individualPlan?.id) {
      // Let's create it first
      individualPlan = await saveChanges();
    }

    // Do it
    const promise = model?.id
      ? IndividualPlansRepository.instance.updateIndividualPlanDiagnostic(individualPlan!, model)
      : IndividualPlansRepository.instance.createIndividualPlanDiagnostic(individualPlan!, model);

    // Save
    promise
      .then((data) => {
        setState((prevState) => ({
          ...prevState,
          individualPlan: data,
          isUpdatingIndividualPlan: false,
          isAddDiagnosticPromptOpen: false,
        }));
        clearModelAndForm();
      })
      .catch((err) => {
        console.error(`Error: ${err}`);
        setState((prevState) => ({
          ...prevState,
          isUpdatingIndividualPlan: false,
          isAddDiagnosticPromptOpen: false,
        }));
        clearModelAndForm();
      });
  }

  function removeDiagnostic() {
    // Start loading
    setState((prevState) => ({
      ...prevState,
      isUpdatingIndividualPlan: true,
    }));

    // Do it
    IndividualPlansRepository.instance
      .removeIndividualPlanDiagnostic(state.individualPlan!, model)
      .finally(() => {
        // Close prompt and stop loading
        setIsRemovingPromptOpen(false);
        clearModelAndForm();
        setState((prevState) => ({
          ...prevState,
          isUpdatingIndividualPlan: false,
          individualPlan: {
            ...prevState.individualPlan!,
            diagnostics: prevState.individualPlan!.diagnostics?.filter(
              (diagnostic) => diagnostic.id !== model.id
            ),
          },
        }));
      });
  }

  return (
    <>
      {(!state.individualPlan ||
        !state.individualPlan.diagnostics ||
        state.individualPlan.diagnostics.length === 0) && (
        <Box className="result-wrapper result-wrapper-center">
          <Box
            className="result-wrapper-empty"
            sx={{
              paddingTop: '32px',
            }}
          >
            <h3 className="title">
              {t(translations.seniors.edit.senior_dcm.individual_plan.empty_state.title)}
            </h3>
            <h5 className="subtitle">
              {t(translations.seniors.edit.senior_dcm.individual_plan.empty_state.subtitle)}
            </h5>
          </Box>
        </Box>
      )}
      {state.individualPlan &&
        state.individualPlan.diagnostics &&
        state.individualPlan.diagnostics.length > 0 && (
          <Box
            sx={{
              marginTop: '16px',
              marginBottom: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {state.individualPlan.diagnostics.map((diagnostic, index) => {
              return (
                <IndividualPlanDiagnosticComponent
                  key={index}
                  state={state}
                  setState={setState}
                  individualPlanDiagnostic={diagnostic}
                  onEdit={() => {
                    // Clear
                    clearModelAndForm(diagnostic);

                    // Open
                    setState((prevState) => ({
                      ...prevState,
                      isAddDiagnosticPromptOpen: true,
                    }));
                  }}
                  onRemove={() => {
                    // Clear
                    clearModelAndForm(diagnostic);

                    // Open
                    setIsRemovingPromptOpen(true);
                  }}
                  canWrite={canWrite}
                />
              );
            })}
          </Box>
        )}

      {state.isAddDiagnosticPromptOpen && (
        <Modal
          open={state.isAddDiagnosticPromptOpen}
          onClose={() => {
            setState((prevState) => ({
              ...prevState,
              isAddDiagnosticPromptOpen: false,
            }));
            clearModelAndForm();
          }}
          maxWidth="sm"
          fullWidth
          preventCloseClick
          title={
            model?.id
              ? t(translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic.edit)
              : t(translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic.title)
          }
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: () => {
                setState((prevState) => ({
                  ...prevState,
                  isAddDiagnosticPromptOpen: false,
                }));
                clearModelAndForm();
              },
              disabled: state.isUpdatingIndividualPlan,
            },
            {
              label: model?.id ? t(translations.modal.save) : t(translations.modal.add),
              type: 'nondestructive',
              onClick: addDiagnostic,
              loading: state.isUpdatingIndividualPlan,
            },
          ]}
        >
          <form>
            <FlatInput
              disabled={state.isUpdatingIndividualPlan}
              value={model.definition}
              label={t(
                translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic.form.definition
                  .label
              )}
              placeholder={t(
                translations.seniors.edit.senior_dcm.individual_plan.add_diagnostic.form.definition
                  .placeholder
              )}
              errors={formValidation?.definition?.errorMessage}
              counter={{
                current: model.definition?.length ?? 0,
                max: DEFINITION_MAX_CHARACTERS,
              }}
              required
              onChange={(event) => {
                clearModelAndForm({
                  ...model,
                  definition: event.target.value,
                });
              }}
            />
          </form>
        </Modal>
      )}
      {isRemovingPromptOpen && (
        <Modal
          open={isRemovingPromptOpen}
          onClose={() => {
            setIsRemovingPromptOpen(false);
            clearModelAndForm();
          }}
          maxWidth="sm"
          fullWidth
          className="delete-modal"
          preventCloseClick
          title={t(
            translations.seniors.edit.senior_dcm.individual_plan.on_diagnostic_remove.header
          )}
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: () => {
                setIsRemovingPromptOpen(false);
                clearModelAndForm();
              },
              disabled: state.isUpdatingIndividualPlan,
            },
            {
              label: t(translations.modal.delete),
              type: 'nondestructive',
              onClick: removeDiagnostic,
              loading: state.isUpdatingIndividualPlan,
            },
          ]}
        >
          <Stack className="container">
            <Typography className="title">
              {t(translations.seniors.edit.senior_dcm.individual_plan.on_diagnostic_remove.title, {
                name: model.definition,
              })}
            </Typography>
            <Typography className="subtitle">
              {t(
                translations.seniors.edit.senior_dcm.individual_plan.on_diagnostic_remove
                  .description
              )}
            </Typography>
          </Stack>
        </Modal>
      )}
    </>
  );
};

export const IndividualPlanScreen = () => {
  // Context
  const appContext: AppContextType = useContext(AppContext);

  // Hooks
  const {isRunningMobile} = useObserveViewport();
  const {t} = useTranslation();
  let {entityId, id}: {entityId: string; id: string} = useParams();
  const sidebarContext = useSeniorSidebar();
  const senior = useMemo(() => {
    return sidebarContext.state.list?.get(id)?.originalData;
  }, [id, sidebarContext.state.list]);
  const {hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Seniors_IndividualPlan);
  const seniorName = useMemo(() => {
    if (!id) return;

    const senior = sidebarContext.state.list?.get(id);
    return senior ? senior.originalData.displayName || senior.originalData?.name : '';
  }, [id, sidebarContext.state.list]);

  // Global State
  const [state, setState] = useState<IndividualPlanScreenState>({
    isLoadingIndividualPlan: true,
    isUpdatingIndividualPlan: false,
    individualPlan: null,
    isAddDiagnosticPromptOpen: false,
  });

  // Individual Plan State
  const snackBarContext = useSnackBar();
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  // Proposal from the AI: kept apart from the plan, never saved without user approval
  const [aiProposal, setAiProposal] = useState<IndividualPlanAiGenerated | null>(null);
  // Mode being saved (null when nothing is being saved) and the "existing plan" popup
  const [applyingMode, setApplyingMode] = useState<IndividualPlanDiagnosticsBulkMode | null>(null);
  const [isApplyPromptOpen, setIsApplyPromptOpen] = useState(false);
  const [isAiGenerateDialogOpen, setIsAiGenerateDialogOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  // Moment (ms) until which the AI button is blocked because of the generations limit
  const [aiBlockedUntil, setAiBlockedUntil] = useState<number | null>(null);
  const [viewCoordinatorUser, setViewCoordinatorUser] = useState<UserPublicInfo | null>(null);
  const [loadingViewCoordinatorUser, setLoadingViewCoordinatorUser] = useState<boolean>(true);
  const [observationsTextareaAnchorEl, setObservationsTextareaAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isArchivingPromptOpen, setIsArchivingPromptOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!aiBlockedUntil) {
      return;
    }
    const timeout = setTimeout(
      () => setAiBlockedUntil(null),
      Math.max(aiBlockedUntil - Date.now(), 0)
    );
    return () => clearTimeout(timeout);
  }, [aiBlockedUntil]);

  const aiGenerateCarePlan = useCallback(
    (focus?: string) => {
      // Prepare state (the existing plan is not touched)
      setIsAiGenerating(true);
      setAiProposal(null);

      // Request
      EntityUserIndividualPlansRepository.instance
        .generateIndividualPlanByAi(entityId, id, focus)
        .then((data) => {
          if (!data.individualPlan?.diagnostics?.length) {
            throw new Error(`Individual Plan was not generated by AI for ${entityId}/${id}`);
          }
          setAiProposal(data);
        })
        .catch((err) => {
          console.error(`Error: ${err}`);
          const labels = translations.seniors.edit.senior_dcm.individual_plan.ai_proposal;

          // 429: the hourly limit of generations was reached, block the button until it resets
          if (err?.response?.status === 429) {
            const retryAfterSeconds = getRetryAfterSeconds(err);
            setAiBlockedUntil(Date.now() + retryAfterSeconds * 1000);
            snackBarContext.setOfficeToastNotification({
              severity: 'warning',
              message: t(labels.rate_limited, {minutes: Math.ceil(retryAfterSeconds / 60)}),
            });
            return;
          }

          // Any other failure (e.g. the AI did not return a usable plan): the user can try again
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(labels.error),
          });
        })
        .finally(() => {
          setIsAiGenerating(false);
        });
    },
    [entityId, id, snackBarContext, t]
  );

  useEffect(() => {
    if (!canWrite) {
      if (state.individualPlan?.coordinatorUserId && !viewCoordinatorUser) {
        // Load user
        UsersRepository.instance
          .getUserPublicInfo(state.individualPlan?.coordinatorUserId)
          .then((user) => {
            setViewCoordinatorUser(user);
          })
          .finally(() => {
            setLoadingViewCoordinatorUser(false);
          });
      } else {
        setLoadingViewCoordinatorUser(false);
      }
    }
  }, [canWrite, state, viewCoordinatorUser]);

  const generateEmptyIndividualPlan = useCallback(() => {
    return {
      entityId,
      userId: id,
      isActive: true,
    };
  }, [entityId, id]);

  useEffect(() => {
    Promise.all([
      IndividualPlansRepository.instance.getActiveIndividualPlans(entityId, id),
      IndividualPlansRepository.instance.getIndividualPlanCares({
        id: '000000000000000000000000',
        entityId,
        userId: id,
        isActive: false,
      }),
    ])
      .then(([individualPlans, individualPlanCares]) => {
        if (individualPlans.length > 0) {
          setState((prevState) => ({
            ...prevState,
            individualPlan: individualPlans[0],
            individualPlanCares,
          }));
        } else {
          // Init a new model
          setState((prevState) => ({
            ...prevState,
            individualPlan: generateEmptyIndividualPlan(),
            individualPlanCares,
          }));
        }
      })
      .finally(() => {
        setState((prevState) => ({
          ...prevState,
          isLoadingIndividualPlan: false,
        }));
      });
  }, [entityId, id, generateEmptyIndividualPlan]);

  async function saveChanges(changedIndividualPlan?: IndividualPlan): Promise<IndividualPlan> {
    // Lock UI
    setState((prevState) => ({
      ...prevState,
      isUpdatingIndividualPlan: true,
    }));

    // Pick plan
    const plan = changedIndividualPlan ?? state.individualPlan;

    // Check plan
    if (!plan) {
      console.error('No Individual Plan to save');
      return Promise.reject('No Individual Plan to save');
    }

    // Do it
    const promise = plan?.id
      ? IndividualPlansRepository.instance.updateIndividualPlan(plan)
      : IndividualPlansRepository.instance.createIndividualPlan(plan);

    try {
      const data = await promise;
      setState((prevState) => ({
        ...prevState,
        individualPlan: data,
        isUpdatingIndividualPlan: false,
      }));
      return data;
    } catch (err) {
      console.error(`Error: ${err}`);
      setState((prevState) => ({
        ...prevState,
        isUpdatingIndividualPlan: false,
      }));
      return Promise.reject(err);
    }
  }

  function requestApproveAiProposal() {
    // Never override an existing plan without asking
    const hasExistingContent =
      aiProposal?.hasExistingContent || !!state.individualPlan?.diagnostics?.length;
    if (hasExistingContent) {
      setIsApplyPromptOpen(true);
    } else {
      approveAiProposal('append');
    }
  }

  // Loads the active plan again (for example, when someone else changed it)
  function reloadIndividualPlan() {
    IndividualPlansRepository.instance
      .getActiveIndividualPlans(entityId, id)
      .then((individualPlans) => {
        if (individualPlans.length > 0) {
          setState((prevState) => ({...prevState, individualPlan: individualPlans[0]}));
        }
      });
  }

  async function approveAiProposal(mode: IndividualPlanDiagnosticsBulkMode) {
    if (!aiProposal || applyingMode) {
      return;
    }

    const labels = translations.seniors.edit.senior_dcm.individual_plan.ai_proposal;
    setApplyingMode(mode);
    try {
      // The plan is only created when it is first saved
      const plan = state.individualPlan?.id ? state.individualPlan : await saveChanges();

      // append: after the existing ones. replace: the previous state stays in the plan history
      const updatedPlan =
        await IndividualPlansRepository.instance.bulkCreateIndividualPlanDiagnostics(plan, {
          mode,
          source: 'ai',
          diagnostics: aiProposal.individualPlan.diagnostics,
        });

      setState((prevState) => ({
        ...prevState,
        individualPlan: updatedPlan,
      }));
      setAiProposal(null);
      setIsApplyPromptOpen(false);
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(mode === 'replace' ? labels.replaced : labels.approved),
      });
    } catch (err: any) {
      console.error(`Error: ${err}`);

      // The proposal stays open so the user can try again
      setIsApplyPromptOpen(false);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(err?.response?.status === 400 ? labels.invalid_references : labels.save_error),
      });
    } finally {
      setApplyingMode(null);
    }
  }

  function archive() {
    // Start loading
    setState((prevState) => ({
      ...prevState,
      isUpdatingIndividualPlan: true,
    }));

    // Do it
    IndividualPlansRepository.instance.archiveIndividualPlan(state.individualPlan!).finally(() => {
      // Close prompt and stop loading
      setIsArchivingPromptOpen(false);
      setState((prevState) => ({
        ...prevState,
        isUpdatingIndividualPlan: false,
        individualPlan: generateEmptyIndividualPlan(),
      }));
    });
  }

  // #region Header Combo Actions

  let headerComboActionsProps: ComboActionsProps | undefined = {
    actions: [],
  };
  if (state.individualPlan?.id) {
    // Anyone who can see the plan can see its history
    headerComboActionsProps?.actions.push({
      label: t(translations.seniors.edit.senior_dcm.individual_plan.history.action),
      icon: {element: FiClock},
      callback: () => {
        setIsHistoryOpen(true);
      },
    });
  }
  if (canWrite && state.individualPlan?.id) {
    headerComboActionsProps?.actions.push({
      label: t(translations.generic_header.actions.archivePlan),
      icon: {element: FiArchive},
      callback: () => {
        setIsArchivingPromptOpen(true);
      },
      disabled: state.isUpdatingIndividualPlan,
    });
  }
  if (headerComboActionsProps.actions.length === 0) {
    headerComboActionsProps = undefined;
  }

  // #endregion

  return (
    <>
      <GenericHeader
        actions={headerComboActionsProps}
        additionalHeight={52}
        isLoading={false}
        pageTitle={isRunningMobile ? t(translations.seniorSideMenu.careAndActivity) : undefined}
        titleCrumbs={[
          {
            label: t(translations.screen_paths.professional_caregiver_seniors),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}`,
          },
          {
            label: senior?.displayName ?? senior?.name ?? '',
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
          },
          {
            label: t(translations.seniorSideMenu.individualPlan),
            link: '',
          },
        ]}
        leftColumn={
          <>
            <Stack direction="column" gap="4px">
              <Stack direction="row" justifyContent="flex-start" alignItems="center">
                <Box
                  sx={{
                    height: '44px',
                  }}
                ></Box>
                <Stack direction="row" justifyContent="flex-end" alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: 'white',
                      fontSize: '16px',
                      lineHeight: '19px',
                      marginRight: '6px',
                    }}
                    noWrap
                  >
                    {t(translations.seniors.edit.senior_dcm.individual_plan.created_at)}
                  </Typography>
                  {state.isLoadingIndividualPlan && <Skeleton width={105} height={40} />}
                  {!state.isLoadingIndividualPlan && (
                    <>
                      {state.individualPlan?.createdAt ? (
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'white',
                            fontSize: '16px',
                            lineHeight: '19px',
                          }}
                          noWrap
                        >
                          {moment(state.individualPlan.createdAt).format('L')}
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: 'white',
                            fontSize: '16px',
                            lineHeight: '19px',
                          }}
                          noWrap
                        >
                          ({t(translations.seniors.edit.senior_dcm.individual_plan.not_defined)})
                        </Typography>
                      )}
                    </>
                  )}
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: 'white',
                      fontSize: '16px',
                      lineHeight: '19px',
                      marginLeft: '6px',
                      marginRight: '6px',
                    }}
                    noWrap
                  >
                    |
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: 'white',
                      fontSize: '16px',
                      lineHeight: '19px',
                      marginRight: '6px',
                    }}
                    noWrap
                  >
                    {t(translations.seniors.edit.senior_dcm.individual_plan.valid_until)}
                  </Typography>
                  {state.isLoadingIndividualPlan && (
                    <Skeleton width={canWrite ? 230 : 105} height={40} />
                  )}
                  {!state.isLoadingIndividualPlan && (
                    <>
                      {canWrite && (
                        <DatePicker
                          disabled={state.isUpdatingIndividualPlan}
                          defaultValue={moment('2024-04-17')}
                          minDate={
                            state.individualPlan?.createdAt
                              ? moment(state.individualPlan?.createdAt)
                              : moment()
                          }
                          value={
                            state.individualPlan?.validUntil
                              ? moment(state.individualPlan?.validUntil)
                              : null
                          }
                          sx={{
                            '& input': {
                              padding: '8px 0px 8px 12px',
                              border: 'none',
                            },
                            fontFamily: 'Roboto, sans-serif',
                            fontWeight: 400,
                            fontSize: '14px',
                            paddingLeft: '0',
                            paddingRight: '0',
                            borderRadius: '5px',
                            backgroundColor:
                              appContext.state.currentThemeMode === ThemeMode.Light
                                ? Pallete.white.base
                                : 'transparent',
                          }}
                          onChange={(date) => {
                            if (state.individualPlan) {
                              const changed: IndividualPlan = {
                                ...state.individualPlan,
                                validUntil:
                                  date &&
                                  date.isValid() &&
                                  date.isAfter(state.individualPlan.createdAt ?? new Date())
                                    ? date.toDate()
                                    : undefined,
                              };

                              // Update UI
                              setState((prevState) => ({
                                ...prevState,
                                individualPlan: changed,
                              }));

                              // Save
                              saveChanges(changed);
                            }
                          }}
                        />
                      )}
                      {!canWrite && (
                        <>
                          {state.individualPlan?.validUntil ? (
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: 'white',
                                fontSize: '16px',
                                lineHeight: '19px',
                              }}
                              noWrap
                            >
                              {moment(state.individualPlan.validUntil).format('L')}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: 'white',
                                fontSize: '16px',
                                lineHeight: '19px',
                              }}
                              noWrap
                            >
                              ({t(translations.seniors.edit.senior_dcm.individual_plan.not_defined)}
                              )
                            </Typography>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Stack>
              </Stack>
              <Stack direction="row" justifyContent="flex-start" alignItems="center">
                <Box
                  sx={{
                    height: '44px',
                  }}
                ></Box>
                <Stack direction="row" justifyContent="flex-start" alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: 'white',
                      fontSize: '16px',
                      lineHeight: '19px',
                      width: {xs: '50px', md: 'auto'},
                      marginRight: '10px',
                    }}
                    noWrap
                  >
                    {t(translations.seniors.edit.senior_dcm.individual_plan.caregiver.coordinator)}:
                  </Typography>
                  {state.isLoadingIndividualPlan && <Skeleton width={234} height={40} />}
                  {!state.isLoadingIndividualPlan && (
                    <>
                      {canWrite ? (
                        <RoleAutocomplete
                          emptyOptionLabel={t(
                            translations.responsibleUserDropdown.noCoordinatorDefined
                          )}
                          disabled={state.isUpdatingIndividualPlan}
                          defaultUserId={state.individualPlan?.coordinatorUserId ?? undefined}
                          onChange={(user: Option | undefined) => {
                            if (state.individualPlan) {
                              const changed: IndividualPlan = {
                                ...state.individualPlan,
                                coordinatorUserId: user?.key === '' ? null : user?.key,
                              };

                              // Update UI
                              setState((prevState) => ({
                                ...prevState,
                                individualPlan: changed,
                              }));

                              // Save
                              saveChanges(changed);
                            }
                          }}
                        />
                      ) : (
                        <>
                          {loadingViewCoordinatorUser && <Skeleton width={234} height={40} />}
                          {!loadingViewCoordinatorUser && (
                            <Tooltip
                              title={t(
                                translations.seniors.edit.senior_dcm.individual_plan.caregiver
                                  .unassigned_tooltip
                              )}
                              placement="bottom"
                              arrow
                            >
                              <Stack
                                flexDirection="row"
                                gap="6px"
                                alignItems="center"
                                sx={{
                                  cursor: 'not-allowed',
                                }}
                              >
                                {viewCoordinatorUser && (
                                  <Avatar
                                    sx={{height: '28px', width: '28px', border: '1px solid white'}}
                                    src={viewCoordinatorUser.picture}
                                    alt={`avatar_${viewCoordinatorUser?.name}`}
                                  />
                                )}
                                <Typography
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '24px',
                                    color: 'white',
                                  }}
                                >
                                  {viewCoordinatorUser
                                    ? viewCoordinatorUser?.name
                                    : t(
                                        translations.seniors.edit.senior_dcm.individual_plan
                                          .caregiver.unassigned
                                      )}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  sx={{
                    marginLeft: '16px',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: 'white',
                      fontSize: '16px',
                      lineHeight: '19px',
                      width: {xs: '50px', md: 'auto'},
                      marginRight: '10px',
                    }}
                    noWrap
                  >
                    {t(translations.seniors.edit.senior_dcm.individual_plan.observations)}:
                  </Typography>
                  {state.isLoadingIndividualPlan && <Skeleton width={234} height={40} />}
                  {!state.isLoadingIndividualPlan && (
                    <ClickAwayListener
                      onClickAway={() => {
                        if (observationsTextareaAnchorEl) {
                          // Close
                          setObservationsTextareaAnchorEl(null);

                          // Save
                          saveChanges();
                        }
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                        }}
                      >
                        {canWrite && (
                          <InputBase
                            disabled={state.isUpdatingIndividualPlan}
                            className="search-input"
                            value={state.individualPlan?.observations ?? ''}
                            sx={{
                              width: '460px',
                              fontFamily: 'Roboto, sans-serif',
                              fontWeight: 400,
                              fontSize: '14px',
                              height: '40px',
                              padding: '0 0 0 15px',
                              borderRadius: '5px',
                              border: `1px solid ${Pallete.blue.lighter}`,
                              backgroundColor:
                                appContext.state.currentThemeMode === ThemeMode.Light
                                  ? Pallete.white.base
                                  : 'transparent',
                              overflow: 'hidden',
                            }}
                            onClick={(event) => {
                              setObservationsTextareaAnchorEl(
                                observationsTextareaAnchorEl ? null : event.currentTarget
                              );
                            }}
                            multiline
                            rows={1}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton aria-label="toggle observations" onClick={() => {}}>
                                  <ExpandMore />
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                        )}
                        {!canWrite && (
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: 'white',
                              fontSize: '14px',
                              lineHeight: '24px',
                              width: '460px',
                              marginRight: '10px',
                            }}
                            noWrap
                            onClick={(event) => {
                              setObservationsTextareaAnchorEl(
                                observationsTextareaAnchorEl ? null : event.currentTarget
                              );
                            }}
                          >
                            {state.individualPlan?.observations ??
                              t(translations.seniors.edit.senior_dcm.individual_plan.not_defined)}
                          </Typography>
                        )}
                        <Popper
                          open={!!observationsTextareaAnchorEl}
                          anchorEl={observationsTextareaAnchorEl}
                          sx={{
                            zIndex: 2,
                            width: '460px',
                            top: '-40px !important',
                            left: '0 !important',
                            '& .MuiBox-root': {
                              borderRadius: '5px',
                              border: `1px solid ${Pallete.blue.lighter}`,
                              paddingTop: '0px',
                              paddingBottom: '0px',
                            },
                          }}
                        >
                          <Box sx={{border: 1, p: 1, bgcolor: 'background.paper'}}>
                            <InputBase
                              multiline
                              rows={8}
                              autoFocus
                              disabled={!canWrite || state.isUpdatingIndividualPlan}
                              sx={{
                                width: '100%',
                                fontFamily: 'Roboto, sans-serif',
                                fontWeight: 400,
                                fontSize: '14px',
                                paddingTop: '8px',
                                paddingBottom: '8px',
                                paddingLeft: '7px',
                              }}
                              value={state.individualPlan?.observations ?? ''}
                              onChange={(event) => {
                                if (state.individualPlan) {
                                  // Update UI
                                  setState((prevState) => ({
                                    ...prevState,
                                    individualPlan: {
                                      ...prevState.individualPlan!,
                                      observations:
                                        event.target.value.trim() === ''
                                          ? null
                                          : event.target.value,
                                    },
                                  }));
                                }
                              }}
                              onFocus={(event) => {
                                const textLength = (state.individualPlan?.observations ?? '')
                                  .length;
                                event.target.setSelectionRange(textLength, textLength);
                              }}
                            />
                          </Box>
                        </Popper>
                      </Box>
                    </ClickAwayListener>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </>
        }
        rightColumn={
          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            {canWrite && (
              <Button
                onClick={() => {
                  // Open
                  setState((prevState) => ({
                    ...prevState,
                    isAddDiagnosticPromptOpen: true,
                  }));
                }}
                variant="inverted"
                size="large"
                startIcon={<Add />}
                disabled={state.isLoadingIndividualPlan || state.isUpdatingIndividualPlan}
                sx={{
                  width: 'auto',
                  height: '40px',
                  letterSpacing: '0.2px',
                  py: '8px',
                  '.MuiButton-startIcon': {
                    margin: isRunningMobile ? 0 : '0 8px 0 -4px',
                  },
                }}
              >
                {!isRunningMobile &&
                  t(translations.seniors.edit.senior_dcm.individual_plan.new_diagnostic)}
              </Button>
            )}
            {canWrite && (
              <Box
                sx={{
                  position: 'relative',
                  top: '-4px',
                }}
              >
                <Tooltip
                  title={
                    aiBlockedUntil
                      ? t(
                          translations.seniors.edit.senior_dcm.individual_plan.ai_proposal
                            .rate_limited,
                          {minutes: Math.max(Math.ceil((aiBlockedUntil - Date.now()) / 60000), 1)}
                        )
                      : ''
                  }
                >
                  <Box component="span" sx={{display: 'inline-flex'}}>
                    <AiButton
                      disabled={
                        state.isLoadingIndividualPlan ||
                        state.isUpdatingIndividualPlan ||
                        !!aiBlockedUntil
                      }
                      isLoading={isAiGenerating}
                      onClick={() => {
                        setIsAiGenerateDialogOpen(true);
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            )}
          </Stack>
        }
      />
      {state.isLoadingIndividualPlan && (
        <Stack
          direction="column"
          sx={{
            marginTop: '16px',
          }}
          gap="16px"
        >
          <Skeleton variant="rectangular" width="100%" height={360} />
          <Skeleton variant="rectangular" width="100%" height={360} />
          <Skeleton variant="rectangular" width="100%" height={360} />
        </Stack>
      )}
      {!state.isLoadingIndividualPlan && (
        <>
          <Box
            sx={{
              marginTop: '16px',
              marginBottom: '24px',
            }}
          />
          <IndividualPlanComponent
            state={state}
            setState={setState}
            saveChanges={saveChanges}
            canWrite={canWrite}
          />
        </>
      )}
      <br></br>
      {isHistoryOpen && state.individualPlan?.id && (
        <IndividualPlanHistoryPanel
          individualPlan={state.individualPlan}
          activityTypes={state.individualPlanCares?.activityTypes}
          canWrite={canWrite}
          onRestored={(restoredPlan) => {
            setState((prevState) => ({...prevState, individualPlan: restoredPlan}));
          }}
          onConflict={reloadIndividualPlan}
          onClose={() => {
            setIsHistoryOpen(false);
          }}
        />
      )}
      {isAiGenerateDialogOpen && (
        <AiGenerateDialog
          onClose={() => {
            setIsAiGenerateDialogOpen(false);
          }}
          onGenerate={(focus) => {
            setIsAiGenerateDialogOpen(false);
            aiGenerateCarePlan(focus);
          }}
        />
      )}
      {aiProposal && (
        <AiProposalPreview
          proposal={aiProposal}
          isApproving={applyingMode !== null}
          onApprove={requestApproveAiProposal}
          onDiscard={() => {
            setAiProposal(null);
          }}
        />
      )}
      {aiProposal && isApplyPromptOpen && (
        <Modal
          open
          onClose={() => {
            setIsApplyPromptOpen(false);
          }}
          maxWidth="sm"
          fullWidth
          preventCloseClick
          title={t(
            translations.seniors.edit.senior_dcm.individual_plan.ai_proposal.apply_prompt.title
          )}
          actionButton={{type: 'close'}}
          actions={[
            {
              label: t(
                translations.seniors.edit.senior_dcm.individual_plan.ai_proposal.apply_prompt
                  .replace
              ),
              type: 'destructive',
              onClick: () => approveAiProposal('replace'),
              disabled: applyingMode === 'append',
              loading: applyingMode === 'replace',
            },
            {
              label: t(
                translations.seniors.edit.senior_dcm.individual_plan.ai_proposal.apply_prompt.append
              ),
              type: 'nondestructive',
              onClick: () => approveAiProposal('append'),
              disabled: applyingMode === 'replace',
              loading: applyingMode === 'append',
            },
          ]}
        >
          <Typography>
            {t(
              translations.seniors.edit.senior_dcm.individual_plan.ai_proposal.apply_prompt
                .description
            )}
          </Typography>
        </Modal>
      )}
      {isArchivingPromptOpen && (
        <Modal
          open={isArchivingPromptOpen}
          onClose={() => {
            setIsArchivingPromptOpen(false);
          }}
          maxWidth="sm"
          fullWidth
          className="delete-modal"
          preventCloseClick
          title={t(translations.generic_header.actions.archivePlan)}
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: () => {
                setIsArchivingPromptOpen(false);
              },
              disabled: state.isUpdatingIndividualPlan,
            },
            {
              label: t(translations.modal.archive),
              type: 'nondestructive',
              onClick: archive,
              loading: state.isUpdatingIndividualPlan,
            },
          ]}
        >
          <Stack className="container">
            <Typography className="title">
              {t(translations.seniors.edit.senior_dcm.individual_plan.on_delete.title, {
                name: seniorName,
              })}
            </Typography>
            <Typography className="subtitle">
              {t(translations.seniors.edit.senior_dcm.individual_plan.on_delete.description)}
            </Typography>
          </Stack>
        </Modal>
      )}
    </>
  );
};
